import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Shared design tokens
const S = {
  bg: "#0f172a",
  border: "#1e293b",
  active: "#0d9488",
  activeShadow: "rgba(13,148,136,0.35)",
  hoverBg: "rgba(13,148,136,0.12)",
  idleIcon: "#64748b",
  activeIcon: "#ffffff",
  hoverIcon: "#0d9488",
  badge: "#ef4444",
  width: 64,
};

function NavItem({ to, icon, label, isActive, unreadCount, onClick }) {
  const [hovered, setHovered] = useState(false);

  const sharedStyle = {
    position: "relative",
    display: "flex", alignItems: "center", justifyContent: "center",
    width: "44px", height: "44px", borderRadius: "12px",
    textDecoration: "none", flexShrink: 0,
    transition: "background 0.15s, box-shadow 0.15s",
    background: isActive ? S.active : hovered ? S.hoverBg : "transparent",
    boxShadow: isActive ? `0 4px 12px ${S.activeShadow}` : "none",
    cursor: "pointer", border: "none",
  };

  if (onClick) {
    return (
      <button
        aria-label={label}
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ ...sharedStyle, background: isActive ? S.active : hovered ? S.hoverBg : "transparent" }}
      >
        <FontAwesomeIcon icon={icon} style={{ fontSize: "18px", color: isActive ? S.activeIcon : hovered ? S.hoverIcon : S.idleIcon, transition: "color 0.15s" }} />
        {unreadCount > 0 && (
          <span style={{ position: "absolute", top: "4px", right: "4px", minWidth: "16px", height: "16px", padding: "0 3px", background: S.badge, color: "#fff", borderRadius: "8px", fontSize: "9px", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", border: `2px solid ${S.bg}`, lineHeight: 1 }}>
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
        {hovered && (
          <span style={{ position: "absolute", left: "52px", top: "50%", transform: "translateY(-50%)", background: "#1e293b", color: "#e2e8f0", border: "1px solid #334155", padding: "5px 10px", borderRadius: "7px", fontSize: "12px", fontWeight: 500, whiteSpace: "nowrap", zIndex: 9999, pointerEvents: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.3)" }}>
            {unreadCount > 0 ? `${label} (${unreadCount})` : label}
          </span>
        )}
      </button>
    );
  }

  return (
    <Link
      to={to}
      aria-label={label}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={sharedStyle}
    >
      <FontAwesomeIcon
        icon={icon}
        style={{
          fontSize: "18px",
          color: isActive ? S.activeIcon : hovered ? S.hoverIcon : S.idleIcon,
          transition: "color 0.15s",
        }}
      />

      {/* Unread badge */}
      {unreadCount > 0 && (
        <span style={{
          position: "absolute", top: "-4px", right: "-4px",
          minWidth: "20px", height: "20px", padding: "0 4px",
          background: "#ef4444", color: "#fff",
          borderRadius: "10px", fontSize: "11px", fontWeight: 800,
          display: "flex", alignItems: "center", justifyContent: "center",
          border: "2px solid #0f172a", lineHeight: 1,
          zIndex: 10, boxShadow: "0 2px 6px rgba(239,68,68,0.5)",
        }}>
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      )}

      {/* Hover tooltip */}
      {hovered && (
        <span style={{
          position: "absolute", left: "52px", top: "50%", transform: "translateY(-50%)",
          background: "#1e293b", color: "#e2e8f0",
          border: "1px solid #334155",
          padding: "5px 10px", borderRadius: "7px",
          fontSize: "12px", fontWeight: 500, whiteSpace: "nowrap",
          zIndex: 9999, pointerEvents: "none",
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
        }}>
          {unreadCount > 0 ? `${label} (${unreadCount})` : label}
        </span>
      )}
    </Link>
  );
}

export default function AppSidebar({ navItems = [], topOffset = "100px", bottomSlot }) {
  const location = useLocation();
  const currentPath = location.pathname;

  return (
    <aside style={{
      position: "fixed", left: 0, top: topOffset, bottom: 0,
      width: `${S.width}px`,
      background: S.bg,
      borderRight: `1px solid ${S.border}`,
      display: "flex", flexDirection: "column", alignItems: "center",
      zIndex: 20,
      boxShadow: "2px 0 12px rgba(0,0,0,0.15)",
    }}>
      {/* Teal accent bar */}
      <div style={{ height: "2px", width: "100%", background: "linear-gradient(90deg,#0d9488,#14b8a6)", flexShrink: 0 }} />

      {/* Nav items */}
      <nav style={{
        flex: 1, display: "flex", flexDirection: "column",
        alignItems: "center", gap: "4px",
        padding: "24px 0 8px", overflowY: "auto", overflowX: "visible",
        width: "100%",
      }}>
        {navItems.map((item) => {
          const isActive = item.exact
            ? currentPath === item.href
            : !!(item.href && (currentPath === item.href || currentPath.startsWith(item.href + "/")));
          return (
            <NavItem
              key={item.href || item.label}
              to={item.href}
              icon={item.icon}
              label={item.label}
              isActive={isActive}
              unreadCount={item.unreadCount || 0}
              onClick={item.onClick}
            />
          );
        })}
      </nav>

      {/* Status indicator at bottom */}
      {bottomSlot && (
        <div style={{ padding: "8px 0 12px", display: "flex", justifyContent: "center", width: "100%" }}>
          {bottomSlot}
        </div>
      )}
    </aside>
  );
}
